import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Loader2, Send, Star } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { formatDate } from '../components/work-permits/constants';
import { API, StarScore, fmtDate } from '../components/five-star/stars';
import { AuditMatrix } from '../components/five-star/AuditMatrix';
import { AuditHistoryDialog } from '../components/five-star/AuditHistoryDialog';

const avg = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);

export default function FiveStarAuditPage() {
  const { permitId } = useParams();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [items, setItems] = useState({});
  const [note, setNote] = useState('');
  const [history, setHistory] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/five-star/permits/${permitId}`).then(r => {
      setCtx(r.data);
      setItems(Object.fromEntries(r.data.criteria.map(c => [c.id, { applicable: true, comment: '', photos: [], scores: {}, notes: {} }])));
    }).catch(e => { toast.error(e.response?.data?.detail || 'Talep yüklenemedi'); navigate('/five-star'); });
  }, [permitId, navigate]);
  const live = useMemo(() => {
    if (!ctx) return null;
    const w = Object.fromEntries(ctx.criteria.map(c => [c.id, c.weight ?? 1]));
    const emp = ctx.employees.map(e => {
      const v = Object.entries(items).filter(([, i]) => i.applicable && i.scores[e.id]);
      const tw = v.reduce((t, [id]) => t + w[id], 0);
      return tw ? v.reduce((t, [id, i]) => t + i.scores[e.id] * w[id], 0) / tw : null;
    }).filter(v => v != null);
    return avg(emp);
  }, [ctx, items]);
  if (!ctx) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const p = ctx.permit;
  const submit = async () => {
    const missing = ctx.criteria.find(c => items[c.id].applicable && ctx.employees.some(e => !items[c.id].scores[e.id]));
    if (missing) { toast.error(`'${missing.name}' başlığında tüm personele yıldız verin veya "Uygulanmaz" seçin`); document.querySelector(`[data-testid="criterion-col-${missing.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/five-star/audits`, { permit_id: permitId, isg_note: note, items: ctx.criteria.map(c => ({ criterion_id: c.id, ...items[c.id] })) });
      toast.success(`Denetim kaydedildi (${data.score} / 5). PDF rapor firmaya e-posta ile gönderiliyor.`);
      navigate('/five-star');
    } catch (e) { toast.error(e.response?.data?.detail || 'Denetim kaydedilemedi'); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-6 pb-24" data-testid="five-star-audit-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/five-star')} data-testid="five-star-back"><ArrowLeft className="mr-1 h-4 w-4" />Geri</Button>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><Star className="h-6 w-6 fill-amber-400 text-amber-400" />Five Star Denetimi · <span className="font-mono text-lg">{p.request_number}</span></h1>
      </div>
      <Card><CardContent className="grid gap-3 p-5 text-sm sm:grid-cols-4">
        <div><p className="text-xs text-slate-500">Firma</p><b>{p.on_behalf_of || p.company_name}</b></div>
        <div><p className="text-xs text-slate-500">Lokasyon</p><b>{p.location_name}{p.store_name ? ` · ${p.store_name}` : ''}</b></div>
        <div><p className="text-xs text-slate-500">İş Tipi / Çeşidi</p><b>{p.is_tipi} / {p.is_cesidi}</b></div>
        <div><p className="text-xs text-slate-500">Tarih</p><b>{formatDate(p.start_date)} – {formatDate(p.end_date)}</b></div>
        <div className="sm:col-span-4"><p className="text-xs text-slate-500">Yapılan İş</p>{p.job_description}</div>
      </CardContent></Card>
      {(ctx.previous?.at || ctx.audits.length > 0) && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-slate-50 px-4 py-2 text-sm" data-testid="five-star-prev-info">
          {ctx.previous?.at && <span className="text-slate-600">Bir önceki değerlendirme: <b className="font-mono">{ctx.previous.request_number}</b> · {fmtDate(ctx.previous.at)} — hücrelerdeki "Önceki" puan ve "Son not" buradan gelir.</span>}
          <Button size="sm" variant="outline" onClick={() => setHistory({ id: permitId, url: `${API}/five-star/companies/${p.behalf_company_id || p.company_id}/audits`, request_number: 'Tüm geçmiş', company_name: p.on_behalf_of || p.company_name })} data-testid="five-star-all-history"><FileText className="mr-1 h-4 w-4" />Firmanın tüm denetim geçmişi</Button>
        </div>
      )}
      <AuditMatrix criteria={ctx.criteria} employees={ctx.employees} items={items} setItem={(id, it) => setItems(s => ({ ...s, [id]: it }))} previous={ctx.previous} />
      <Card className="border-amber-300 bg-amber-50/50"><CardContent className="space-y-2 p-5">
        <p className="font-semibold text-slate-900">İSG Açıklaması / Yapılması Gerekenler</p>
        <Textarea value={note} onChange={e => setNote(e.target.value)} rows={4} placeholder="Firmaya iletilecek uyarı ve yapılması gerekenler (isteğe bağlı)" className="bg-white" data-testid="five-star-note" />
      </CardContent></Card>
      <AuditHistoryDialog permit={history} onClose={() => setHistory(null)} />
      <div className="fixed inset-x-0 bottom-16 lg:bottom-0 z-30 border-t bg-white/95 px-6 py-3 backdrop-blur lg:left-64">
        <div className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-2 text-sm text-slate-600">Anlık puan: <StarScore value={live} size="lg" testId="five-star-live-score" /></span>
          <Button onClick={submit} disabled={saving} className="bg-amber-500 hover:bg-amber-600" data-testid="five-star-submit">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Kaydet ve Firmaya Gönder</Button>
        </div>
      </div>
    </div>
  );
}
